<script setup>
import { useTheme } from '../composables/useTheme';
import { HrIcon, HrTextField } from '../primitives';

/**
 * Search bar — the M3 search container for narrowing a collection down to what the reader wants.
 *
 * Not a form field, and deliberately unlike one. A text field asks the reader to supply a value
 * that will be saved; a search bar is a lens over something already on screen, and nothing it holds
 * is ever submitted. So it carries M3's search container instead of a field's: a pill on a raised
 * surface with a leading search icon, no floating label, no outline, and no room reserved beneath
 * it for a hint or an error, because there is nothing to validate.
 *
 * The placeholder says what is being searched — "Search clients" — rather than the bare word
 * "Search", since a screen may hold more than one collection.
 *
 * Outside the design-system theme it is simply an outlined field. M3's search container is an M3
 * idea; the legacy theme has no equivalent, and a pill on a surface it does not share reads as a
 * control from somewhere else.
 *
 * It is one height, and it is the toolbar's rather than M3's 56dp search bar. That size is drawn
 * for a search that is a screen's primary affordance; this one shares a row with the controls that
 * shape the same collection, and at 56 it outweighed them. There is no size prop, because nothing
 * in the app yet has the other job.
 *
 * @status recommended
 * @group forms-input
 * @useWhen Filtering a list, table or board down to matching records
 * @avoidWhen Collecting a value the reader is submitting — that is HrTextField
 * @related HrTextField
 * @a11y The input carries type="search", so screen readers announce it as a search field and
 *   browsers offer their own clear affordance alongside the one drawn here.
 */
defineOptions({ name: 'HrSearchBar' });

/*
 * The legacy theme has no search container of its own, so the field wears the shape every other
 * field wears there. `filled` is switched off alongside, because the two are separate Quasar flags
 * and setting one does not clear the other — and `dense`, because a full-height field towers over
 * the chips on the row beneath it.
 * @todo Remove when the legacy theme is removed.
 */
const LEGACY_PROPS = { outlined: true, filled: false, dense: true };

defineProps({
  /** Current search term. */
  modelValue: {
    type: String,
    default: '',
  },
  /**
   * Placeholder naming what is being searched, e.g. "Search clients".
   * @example Search clients
   */
  placeholder: {
    type: String,
    default: null,
  },
  /**
   * Milliseconds to wait after the last keystroke before emitting.
   *
   * Defaulted rather than left to callers: every consumer re-filters on each emit, and a search bar
   * that emits per keystroke re-renders the collection under the reader's hands.
   */
  debounce: {
    type: [Number, String],
    default: 300,
  },
  /**
   * Focuses the bar on mount. For a surface that opened to be searched, such as a picker dialog —
   * not a page the reader came to for something else.
   */
  autofocus: {
    type: Boolean,
    default: false,
  },
  /** Prevents interaction and dims the bar. */
  disable: {
    type: Boolean,
    default: false,
  },
});

defineEmits({
  /** Fired when the search term changes, after `debounce` has elapsed. */
  'update:modelValue': null,
});

const { hasTheme } = useTheme();
</script>

<template>
  <hr-text-field
    :class="['hr-search-bar', hasTheme && 'hr-search-bar--themed']"
    type="search"
    variant="filled"
    :legacy-props="LEGACY_PROPS"
    clearable
    hide-bottom-space
    :model-value="modelValue"
    :placeholder="placeholder"
    :debounce="debounce"
    :autofocus="autofocus"
    :disable="disable"
    @update:model-value="$emit('update:modelValue', $event ?? '')"
  >
    <template #prepend>
      <hr-icon
        name="search"
        size="sm"
      />
    </template>
  </hr-text-field>
</template>

<style lang="scss" scoped>
/*
 * The filled text field is the nearest container the design system has, but M3's search container
 * is a different shape on a different surface, and it carries none of a field's validation
 * furniture. The rules below are the difference, not decoration.
 */
.hr-search-bar--themed :deep(.q-field__control) {
  // min-height as well: q-field floors the control at its own 56px, which would win over height.
  height: var(--app-comp-search-bar-container-height);
  min-height: var(--app-comp-search-bar-container-height);
  padding-inline: var(--app-comp-search-bar-container-padding);
  border-radius: var(--app-comp-search-bar-shape);
  /*
   * M3's search container is one tone above the page. A surface that is itself raised says what
   * one tone above it is, since a fill matched to the page all but disappears on a dialog.
   */
  background: var(--hr-search-bar-container, var(--app-surface-container-high));
}

/*
 * q-field fixes the marginal columns at the height of its own 56px control, so in a 40px one the
 * icon is centred on a taller box than it sits in and hangs out of the pill.
 */
.hr-search-bar--themed :deep(.q-field__marginal) {
  height: var(--app-comp-search-bar-container-height);
}

/*
 * ::before carries the filled field's underline; the pill has no underline, so it keeps only the
 * hover state layer and takes the container's radius so the layer does not square off the corners.
 */
.hr-search-bar--themed :deep(.q-field__control::before) {
  border-bottom: none;
  border-radius: inherit;
}

// ::after is the focus underline, which a search container does not have at all.
.hr-search-bar--themed :deep(.q-field__control::after) {
  display: none;
}

/*
 * Body medium rather than the search bar's body large, tracking the shorter container. The padding
 * goes because q-field reserves height above the input for a floating label, and there is no label.
 */
.hr-search-bar--themed :deep(.q-field__native) {
  padding: 0;
  min-height: 0;
  font-size: var(--app-type-body-medium-size);
  line-height: var(--app-type-body-medium-line-height);
  letter-spacing: var(--app-type-body-medium-tracking);
  color: var(--app-on-surface);
}

.hr-search-bar--themed :deep(.q-field__native::placeholder) {
  color: var(--app-on-surface-variant);
  opacity: 1;
}

.hr-search-bar--themed :deep(.q-field__prepend) {
  padding-inline-end: var(--app-comp-search-bar-icon-gap);
  color: var(--app-on-surface-variant);
}
</style>
